/**
 * Service booking summary for the appointment details popup.
 */

import React from 'react';
import { Card, Space, Tag, Typography, theme } from 'antd';
import { ResourceAllocationTag } from '../ResourceAllocationTag';
import { ScheduleOutlined } from '@ant-design/icons';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { Appointment } from '@/api/mcp-appointments';
import { useGetServiceQuery } from '@/api/mcp-appointments';
import {
  getCalendarAppointmentDurationMinutes,
  isServiceCalendarBooking
} from '../calendar/calendarUtils';

const { Text } = Typography;

interface ServiceBookingDetailsSectionProps {
  appointment: Appointment;
  visible: boolean;
}

export const ServiceBookingDetailsSection: React.FC<ServiceBookingDetailsSectionProps> = ({
  appointment,
  visible
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('manage');

  const serviceId = appointment.serviceId ?? appointment.service?.id;
  const shouldFetch = visible && !!serviceId && !appointment.service?.name;

  const { data: fetchedService, isLoading } = useGetServiceQuery(serviceId ?? '', shouldFetch);

  if (!isServiceCalendarBooking(appointment) && !serviceId) {
    return null;
  }

  const serviceName = appointment.service?.name ?? fetchedService?.name;
  const confirmationMode = appointment.service?.confirmationMode ?? fetchedService?.confirmationMode;
  const durationMinutes = getCalendarAppointmentDurationMinutes(appointment);
  const allocations = appointment.allocatedResources ?? [];

  const confirmationLabel =
    confirmationMode === 'DIRECT'
      ? t('appointmentComponents.details.service.confirmationDirect')
      : confirmationMode === 'NEEDS_APPROVAL'
        ? t('appointmentComponents.details.service.confirmationNeedsApproval')
        : null;

  return (
    <Card
      size="small"
      style={{
        backgroundColor: token.colorFillQuaternary,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG
      }}
      styles={{ body: { padding: '12px 16px' } }}
    >
      <Space size={8} style={{ marginBottom: 8 }}>
        <ScheduleOutlined style={{ color: token.colorPrimary }} />
        <Text type="secondary" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {t('appointmentComponents.details.service.bookingType')}
        </Text>
      </Space>

      {isLoading && !serviceName ? (
        <Text type="secondary">{t('appointmentComponents.details.service.loading')}</Text>
      ) : (
        <>
          <div style={{ marginBottom: 8 }}>
            {serviceId && serviceName ? (
              <Link
                to="/manage/company-services/$serviceId/manage"
                params={{ serviceId } as { serviceId: string }}
              >
                <Text strong style={{ fontSize: 15 }}>{serviceName}</Text>
              </Link>
            ) : (
              <Text strong style={{ fontSize: 15 }}>
                {serviceName ?? serviceId ?? '—'}
              </Text>
            )}
          </div>

          <Space size={[4, 4]} wrap style={{ marginBottom: allocations.length > 0 ? 8 : 0 }}>
            <Tag style={{ margin: 0 }}>
              {t('appointmentComponents.details.durationMinutes', { count: durationMinutes })}
            </Tag>
            {confirmationLabel ? (
              <Tag color={confirmationMode === 'DIRECT' ? 'green' : 'gold'} style={{ margin: 0 }}>
                {confirmationLabel}
              </Tag>
            ) : null}
          </Space>

          {allocations.length > 0 ? (
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                {t('appointmentComponents.details.service.assignedResources')}
              </Text>
              <Space size={[4, 4]} wrap>
                {allocations.map((resource) => (
                  <ResourceAllocationTag
                    key={resource.resourceId}
                    resourceId={resource.resourceId}
                    name={resource.name}
                    suffix={
                      <>
                        {resource.isPrimary
                          ? ` · ${t('appointmentComponents.details.service.primary')}`
                          : ''}
                        {resource.isHidden
                          ? ` ${t('appointmentComponents.calendar.appointment.hiddenSuffix')}`
                          : ''}
                      </>
                    }
                  />
                ))}
              </Space>
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
};
